import { ArrayParam, QueryParamConfig, StringParam, useQueryParams } from 'use-query-params'
import { useTranslation } from 'react-i18next'

import { useBreadcrumbs } from '../../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'

import { useDidMountEffect, useQueryList } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { FundraisersTable } from './FundraisersTable/FundraisersTable'
import type {
  FundraiserStatus,
  FundraiserVisibility,
  GetFundraisersListProps
} from '@percent/partner-dashboard/services/fundraisers/fundraisersService.types'
import { PartnerConfigProvider } from '@percent/partner-dashboard/context/config/PartnerConfigProvider'
import { PartnerConfigKey } from '@percent/partner-dashboard/context/config/PartnerConfigProvider.types'
import { TrueFalseBooleanParam } from '@percent/partner-dashboard/common/queryParams/queryParams'
import { TableColumnSortDirection } from '@percent/lemonade'

type SortField = 'title' | 'createdAt' | 'amount' | 'featuredAt'

export function FundraisersContainer() {
  const { fundraisersService } = useServices()
  const { t } = useTranslation()
  useBreadcrumbs([
    {
      label: t('breadcrumb.fundraisers')
    }
  ])

  const [queryParams, setQueryParams] = useQueryParams(
    {
      status: ArrayParam,
      query: StringParam,
      featuredOnly: TrueFalseBooleanParam,
      publicFundraisersOnly: TrueFalseBooleanParam,
      visibility: ArrayParam,
      sort: StringParam as QueryParamConfig<SortField | null | undefined>,
      direction: StringParam as QueryParamConfig<TableColumnSortDirection | null | undefined>
    },
    {}
  )

  const { featuredOnly, publicFundraisersOnly, visibility, ...restQueryParams } = queryParams

  const listParams: GetFundraisersListProps = {
    ...restQueryParams,
    status: queryParams?.status as FundraiserStatus[],
    featuredOnly: !!queryParams?.featuredOnly,
    visibility: queryParams?.visibility as FundraiserVisibility | undefined,
    sort: queryParams?.sort,
    direction: queryParams?.direction
  }

  const [{ data, isLoading, errorMessage, totalResults }, { query, nextPage, previousPage, refresh }] = useQueryList(
    fundraisersService.getFundraisersList,
    listParams
  )

  useDidMountEffect(() => query(listParams), [queryParams, query])

  const filteredData = queryParams?.publicFundraisersOnly
    ? data?.filter(fundraiser => fundraiser.visibility === 'public') || null
    : data

  const filteredTotalResults = queryParams?.publicFundraisersOnly ? filteredData?.length || 0 : totalResults

  return (
    <PartnerConfigProvider partnerConfigKey={PartnerConfigKey.PREFERRED_COUNTRIES}>
      <FundraisersTable
        data={filteredData}
        isLoading={isLoading}
        totalResults={filteredTotalResults}
        errorMessage={errorMessage}
        nextPage={nextPage}
        previousPage={previousPage}
        queryParams={queryParams}
        setQueryParams={setQueryParams}
        refresh={refresh}
      />
    </PartnerConfigProvider>
  )
}
